/**
 * TemplateGallery Component (Stories 5.3, 5.14, AC #1, #3, #5)
 *
 * Shows a compact "Browse Templates" card. When the user clicks
 * "Pick a Template", expands to show the full template list.
 */

import { useMemo, useState } from "react";
import { BrowsersIcon } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";
import { TemplateCard } from "./TemplateCard";
import { EditTemplateDialog } from "./EditTemplateDialog";
import { DeleteTemplateDialog } from "./DeleteTemplateDialog";
import { getRecommendedTemplates } from "../../lib/templateUtils";
import type { ParsedTemplate } from "../../lib/templateUtils";

interface TemplateGalleryProps {
  templates: ParsedTemplate[];
  connectedPlatforms: string[];
  isLoading: boolean;
  onSelectTemplate: (templateId: string) => void;
  clientId: string;
  /** Optional content rendered on the right side of the header (e.g., client selector). */
  headerRight?: React.ReactNode;
  /** Called when user clicks "Preview" on a template card overlay. */
  onPreviewTemplate?: (templateId: string) => void;
  /** Whether the gallery is expanded (controlled by parent). */
  expanded?: boolean;
  /** Called when user clicks "Pick a Template". */
  onExpand?: () => void;
}

export function TemplateGallery({
  templates,
  connectedPlatforms,
  isLoading,
  onSelectTemplate,
  clientId,
  headerRight,
  onPreviewTemplate,
  expanded: controlledExpanded,
  onExpand,
}: TemplateGalleryProps) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded ?? internalExpanded;

  // Edit/Delete dialog state
  const [editTarget, setEditTarget] = useState<ParsedTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ParsedTemplate | null>(null);

  const recommendedTemplates = getRecommendedTemplates(
    templates,
    connectedPlatforms
  );

  const recommendedIds = useMemo(
    () => new Set(recommendedTemplates.map((t) => t.id)),
    [recommendedTemplates]
  );

  // Compact card — shown before expanding
  if (!expanded) {
    return (
      <div
        data-testid="template-gallery"
        className={`relative overflow-hidden rounded-[2rem] bg-card px-6 py-6 h-full shadow-sm transition-opacity duration-300 ${!clientId ? "opacity-50 pointer-events-none" : ""}`}
        style={{ border: "0.5px solid var(--hairline)" }}
      >
        <div className="flex flex-col items-center text-center gap-3 h-full">
          <BrowsersIcon size={28} className="text-foreground" />
          <div>
            <h3 className="text-base font-semibold text-foreground">Browse Templates</h3>
            <p className="text-sm text-foreground/70 mt-1">
              Select from ready-made report templates for the metrics that matter most to your clients.
            </p>
          </div>
          {templates.length > 0 && (
            <p className="text-sm text-foreground">
              {templates.length} templates available
            </p>
          )}
          <Button variant="brand" className="w-full px-8 text-sm rounded-2xl mt-auto" onClick={() => onExpand ? onExpand() : setInternalExpanded(true)}>
            <BrowsersIcon size={16} weight="fill" className="mr-1.5" />
            Pick a Template
          </Button>
        </div>
      </div>
    );
  }

  // Expanded — full template list
  return (
    <div
      data-testid="template-gallery"
      className="rounded-[2rem] px-6 py-5 transition-all duration-300 bg-card shadow-sm"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      {/* Optional header right */}
      {headerRight && (
        <div className="flex items-center justify-end mb-4">
          {headerRight}
        </div>
      )}

      {/* Template grid */}
      <div data-testid="template-grid">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-lg border border-l-[3px] border-l-muted bg-card overflow-hidden animate-pulse"
                data-testid="template-skeleton"
              >
                <div className="px-4 py-3 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-7 bg-muted rounded w-full mt-3" />
                </div>
              </div>
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div
            className="text-center py-12"
            data-testid="template-empty-state"
          >
            <p className="text-muted-foreground">No templates found</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 overflow-y-auto max-h-[400px] pb-2">
            {templates.map((template) => (
              <div key={template.id}>
                <TemplateCard
                  template={template}
                  connectedPlatforms={connectedPlatforms}
                  onSelect={onSelectTemplate}
                  onPreview={onPreviewTemplate}
                  onEdit={setEditTarget}
                  onDelete={setDeleteTarget}
                  isRecommended={recommendedIds.has(template.id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit dialog */}
      {editTarget && (
        <EditTemplateDialog
          open={!!editTarget}
          onOpenChange={(open) => !open && setEditTarget(null)}
          templateId={editTarget.id}
          initialName={editTarget.name}
          initialDescription={editTarget.description}
          initialCategory={editTarget.category}
        />
      )}

      {/* Delete dialog */}
      {deleteTarget && (
        <DeleteTemplateDialog
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          templateId={deleteTarget.id}
          templateName={deleteTarget.name}
        />
      )}
    </div>
  );
}
